/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useRef, useState, useTransition } from 'react';
import useApi from '@/lib/api/useApi';
import ChallengeContext from '@/components/ChallengePortfolio/NormalView/Challenge/ChallengeView/ChallengeContext';
import { useDebounce } from 'use-debounce';
import { type User } from '@/lib/types';

const MINIMUM_QUERY_LENGTH = 3;

const useUserAutocomplete = (untrimmedQuery: string): User[] => {
  const query = untrimmedQuery.trim();

  const api = useApi();
  const [challenge] = useContext(ChallengeContext);

  const [debouncedQuery] = useDebounce(query, 500);
  const latestRequestIndex = useRef(0);

  const [, startTransition] = useTransition();
  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState<
    User[] | null
  >(null);

  const { current: userAutocompleteCache } = useRef(new Map<string, User[]>());

  // Skip requests when any set of leading query characters has already led to
  // 0 results because adding more characters will also not yield results.
  const canSkipRequest = (query: string): boolean => {
    for (let i = MINIMUM_QUERY_LENGTH; i < query.length; i++) {
      const previousQuery = query.slice(0, i);

      if (
        userAutocompleteCache.has(previousQuery) &&
        userAutocompleteCache.get(previousQuery)!.length === 0
      ) {
        return true;
      }
    }

    return false;
  };

  useEffect((): void => {
    if (query.length < MINIMUM_QUERY_LENGTH) {
      startTransition(() => {
        setAutocompleteSuggestions(null);
      });

      return;
    }

    if (userAutocompleteCache.has(query)) {
      startTransition(() => {
        setAutocompleteSuggestions(userAutocompleteCache.get(query)!);
      });

      return;
    }

    if (canSkipRequest(query)) {
      userAutocompleteCache.set(query, []);

      return;
    }

    const requestIndex = ++latestRequestIndex.current;

    const refreshAutocompleteSuggestions = async (): Promise<void> => {
      const newAutocompleteSuggestions = await api.get<User[]>(
        `/challenge-portfolio/challenges/${challenge.id}/users/autocomplete?query=${encodeURIComponent(query)}`,
      );

      userAutocompleteCache.set(query, newAutocompleteSuggestions);

      // This prevents request race conditions
      if (requestIndex !== latestRequestIndex.current) {
        return;
      }

      startTransition(() => {
        setAutocompleteSuggestions(newAutocompleteSuggestions);
      });
    };

    refreshAutocompleteSuggestions();
  }, [debouncedQuery]);

  if (
    query.length < MINIMUM_QUERY_LENGTH ||
    autocompleteSuggestions === null ||
    autocompleteSuggestions.length === 0
  ) {
    return [];
  }

  return autocompleteSuggestions;
};

export default useUserAutocomplete;
